import { CurrencyInput, QuantitySelector } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import type { DocumentDiscountType } from '@trellis/shared'

import { cn } from '@/lib/utils'

export interface DocumentDiscountControlProps {
  discountType: DocumentDiscountType
  discount: number
  onChange: (discountType: DocumentDiscountType, discount: number) => void
  currency: string
  /** FIXED discount dollar amount exceeds the line-items subtotal. */
  exceedsSubtotal: boolean
}

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'PERCENTAGE', label: '% Percentage' },
  { value: 'FIXED', label: '$ Fixed' },
] as const

export function DocumentDiscountControl({
  discountType,
  discount,
  onChange,
  currency,
  exceedsSubtotal,
}: DocumentDiscountControlProps) {
  /**
   * Untouched documents carry discountType 'NONE' — render that as Percentage since
   * discount defaults to 0, a no-op either way.
   */
  const displayType = discountType === 'NONE' ? 'PERCENTAGE' : discountType
  const isFixed = displayType === 'FIXED'

  return (
    <div className="space-y-3">
      <Label htmlFor="document-discount-amount">Discount</Label>

      <div className="flex flex-col gap-3 @md:flex-row @md:items-center">
        <div className="flex w-fit gap-1 rounded-lg border p-1">
          {DISCOUNT_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value, 0)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                displayType === option.value
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {isFixed ? (
            <CurrencyInput
              id="document-discount-amount"
              value={discount}
              onChange={(discountValue) => onChange('FIXED', discountValue)}
              currency={currency}
              min={0}
              ariaLabel="Discount amount"
            />
          ) : (
            <QuantitySelector
              id="document-discount-amount"
              value={discount}
              min={0}
              max={100}
              step={1}
              onChange={(discountValue) => onChange('PERCENTAGE', discountValue)}
              ariaLabel="Discount percentage"
            />
          )}
        </div>
      </div>

      {exceedsSubtotal && (
        <p className="text-destructive-text text-xs">
          Discount exceeds subtotal — total will be $0.00
        </p>
      )}
    </div>
  )
}
